import {
    BarChartOutlined,
    ExperimentOutlined,
    LineChartOutlined,
    TableOutlined,
    RocketOutlined,
    GoldOutlined,
    UserOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    InputNumber,
    Row,
    Segmented,
    Select,
    Slider,
    Space,
    Statistic,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import Highcharts from 'highcharts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CashFlowChart,
    ChartHint,
    ChartWrapper,
    ContractLifecycleChart,
    CustomerSegmentChart,
    DailyContractCountChart,
    DailyContractValueChart,
    DailyMetalContractCountChart,
    DailyMetalContractValueChart,
    DailyPaidContractsCountChart,
    DailyPaidContractsValueChart,
    PaidCommissionCountChart,
    PaidCommissionValueChart,
    PaidContractCommissionChart,
    TotalInterestPaidChart,
    ForecastChart,
    GoldValueMultiChart,
    ExpiredGoldPctChart,
    MetalDistributionChart,
    MetalsIn24kChart,
    NonMetalContractCountChart,
    NonMetalContractValueChart,
    InventoryTrendChart,
    OperationsByHourChart,
    OperationsByEmployeeChart,
    ExpensesByTypeChart,
    MonthlyExpensesChart,
    PortfolioFluxChart,
    GoldPriceChangeSimulationChart,
    PriceSimulationChart,
    CashDemandProjectionChart,
    ProfitabilityChart,
    RetentionChart,
    RiskGaugeChart,
    SeasonalityChart,
    WeeklyPatternChart,
    MonthlyNewClientsChart,
    MonthlyClientActivityChart,
} from '../components/charts';
import {
    AdvancedAnalyticsResponse,
    BusinessIntelligenceResponse,
    type GoldPriceChangeSimulationResponse,
    type GoldPriceSimulationKaratBreakdown,
    type GoldValueResponse,
    type MonthlyExpiredGoldPctData,
    type CashDemandProjectionResponse,
    useApi,
} from '../contexts/ApiContext';
import type { ChartData } from '../components/charts/chartConfigUtils';
import type { ClientStatisticsResponse } from '../domain/statisticsClients';
import type {
    InventoryTrendChartKind,
    InventoryTrendsResponse,
} from '../domain/statisticsInventory';
import type { OperationsResponse } from '../domain/statisticsOperations';
import type { ExpensesResponse, GoodsSalesResponse } from '../domain/statisticsExpenses';
import CurrencyValueChart from '../components/charts/CurrencyValueChart';
import { buildPortfolioFluxPoints } from '../domain/portfolioFlux';
import type { StatisticsCurrency } from '../domain/statisticsReferenceCurrency';
import useQuery from '../hooks/useQuery';

import './Statistics.compressed.css';

interface InventoryTrendCardProps {
    readonly kind: InventoryTrendChartKind;
    /** Key the editable target is persisted under in the `averages` table. */
    readonly chartSeries: string;
    readonly titleKey: string;
    readonly titleFallback: string;
    /** Omitted on the two value cards, which carry no description in the design. */
    readonly descriptionKey?: string;
    readonly descriptionFallback?: string;
    readonly data: readonly ChartData[];
    readonly loading: boolean;
    /**
     * Whether this series is money, which is one decision affecting two places: the wrapper
     * converts its EUR-canonical target onto the displayed scale, and the chart suffixes its
     * axis with the currency. Count, grams and percentage series set this false and are
     * unaffected by the currency toggle.
     */
    readonly money: boolean;
    readonly currency: StatisticsCurrency;
    readonly exchangeRate: number;
}

/**
 * One card on the Inventory sub-tab: title, optional description, editable target and chart.
 *
 * The five cards differ only in their labels, their series key, which kind they plot and whether
 * they are money — everything else was repeated verbatim five times, which is what SonarQube
 * flagged as two 23-line duplicate blocks. The shape now lives here once.
 *
 * `currency` and `exchangeRate` are passed to `ChartWrapper` unconditionally and gated by
 * `convertCurrency` alone. That is not a shortcut: `referenceDisplayFactor` returns 1 whenever
 * `convertCurrency` is false, so a non-money card behaves exactly as it did when it omitted both
 * props. The chart is gated the same way for the same reason — `valueSuffix` never consults the
 * currency for a grams, items or percentage unit.
 */
function InventoryTrendCard({
    kind,
    chartSeries,
    titleKey,
    titleFallback,
    descriptionKey,
    descriptionFallback,
    data,
    loading,
    money,
    currency,
    exchangeRate,
}: InventoryTrendCardProps) {
    const { t } = useTranslation();

    const description =
        descriptionKey === undefined ? undefined : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t(descriptionKey, descriptionFallback ?? '')}
            </Typography.Text>
        );

    return (
        <Card title={t(titleKey, titleFallback)} extra={description}>
            <ChartWrapper
                chartSeries={chartSeries}
                currency={currency}
                exchangeRate={exchangeRate}
                convertCurrency={money}
            >
                <InventoryTrendChart
                    kind={kind}
                    data={data}
                    loading={loading}
                    currency={currency}
                />
            </ChartWrapper>
        </Card>
    );
}

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface StatisticsData {
    // `projected` marks the running month of a monthly series, whose partial
    // totals the backend extrapolates to a full month (Delphi parity).
    dailyContractValue: Array<{ date: string; value: number; projected?: boolean }>;
    dailyContractCount: Array<{ date: string; count: number; projected?: boolean }>;
    nonMetalContractValue: Array<{ date: string; value: number }>;
    nonMetalContractCount: Array<{ date: string; count: number }>;
    metalContractValue: Array<{ date: string; value: number }>;
    metalContractCount: Array<{ date: string; count: number }>;
    topClients: Array<{ name: string; contracts: number; value: number }>;
    metalsIn24k: Array<{ date: string; grams: number }>;
    dailyPaidContractsValue: Array<{ date: string; value: number }>;
    dailyPaidContractsCount: Array<{ date: string; count: number }>;
    paidCommissionValue: Array<{ date: string; value: number }>;
    paidCommissionCount: Array<{ date: string; value: number }>;
    paidContractCommission: Array<{ date: string; value: number }>;
    totalInterestPaid: Array<{ date: string; value: number }>;
}

interface ClientData {
    name: string;
    contracts: number;
    value: number;
}

// Types for table data are straightforward objects with date/value or date/count

const getHealthColor = (score: number): string => {
    if (score >= 70) return '#52c41a';
    if (score >= 40) return '#faad14';
    return '#ff4d4f';
};

const getDefaultRateColor = (rate: number): string => {
    if (rate <= 10) return '#52c41a';
    if (rate <= 20) return '#faad14';
    return '#ff4d4f';
};

const getRiskTrendColor = (trend: string): string => {
    if (trend === 'improving') return '#52c41a';
    if (trend === 'stable') return '#1890ff';
    return '#ff4d4f';
};

const getRiskTrendTagColor = (trend: string): string => {
    if (trend === 'improving') return 'success';
    if (trend === 'stable') return 'processing';
    return 'error';
};

const getRiskTrendArrow = (trend: string): string => {
    if (trend === 'improving') return '↑';
    if (trend === 'stable') return '→';
    return '↓';
};

const getAdditionalCashTagColor = (value: number): string => {
    if (value > 0) return 'red';
    if (value < 0) return 'green';
    return 'default';
};

const formatDateByTimeframe = (date: string, timeframe: string): string => {
    const dateObj = dayjs(date);
    switch (timeframe) {
        case 'yearly':
            return dateObj.format('YYYY');
        case 'weekly':
            return dateObj.format('YYYY-MM-DD');
        case 'monthly':
            return dateObj.format('YYYY-MM');
        default:
            return dateObj.format('YYYY-MM-DD');
    }
};

interface SimulationSummaryData {
    totalNetGoldGrams: number;
    totalContracts: number;
    currentTotalValue: number;
    newTotalValue: number;
    totalAdditionalCash: number;
}

const SimulationTableSummaryRow = ({
    simulatedSummary,
    currencyLabel,
}: {
    simulatedSummary: SimulationSummaryData | null;
    currencyLabel: string;
}) => {
    const { t } = useTranslation();
    if (!simulatedSummary) return null;
    return (
        <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
                <Typography.Text strong>
                    {t('statistics.simulations.total', 'TOTAL')}
                </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>
                    {simulatedSummary.totalNetGoldGrams.toFixed(2)}
                </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
                <Typography.Text strong>{simulatedSummary.totalContracts}</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} />
            <Table.Summary.Cell index={4} />
            <Table.Summary.Cell index={5} align="right">
                <Typography.Text strong>
                    {simulatedSummary.currentTotalValue.toLocaleString('ro-RO')} {currencyLabel}
                </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
                <Typography.Text strong>
                    {simulatedSummary.newTotalValue.toLocaleString('ro-RO')} {currencyLabel}
                </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="right">
                <Tag color={getAdditionalCashTagColor(simulatedSummary.totalAdditionalCash)}>
                    <Typography.Text strong>
                        {simulatedSummary.totalAdditionalCash > 0 ? '+' : ''}
                        {simulatedSummary.totalAdditionalCash.toLocaleString('ro-RO')}{' '}
                        {currencyLabel}
                    </Typography.Text>
                </Tag>
            </Table.Summary.Cell>
        </Table.Summary.Row>
    );
};

const buildTimeframedTitle = (
    t: (key: string, fallback: string) => string,
    timeframe: string,
    baseKey: string,
    fallback: string,
): string => {
    const capitalized = baseKey.charAt(0).toUpperCase() + baseKey.slice(1);
    const timeframeSpecificKey = `statistics.cards.${timeframe}${capitalized}`;
    const tfCard = t(timeframeSpecificKey, '');
    if (tfCard) return tfCard;

    const timeframeKey = `statistics.${timeframe}.${baseKey}`;
    const translated = t(timeframeKey, '');
    if (translated) return translated;

    const timeframeLabel = t(
        `statistics.timeframe.${timeframe}`,
        timeframe.charAt(0).toUpperCase() + timeframe.slice(1),
    );
    const genericLabel = t(`statistics.cards.${baseKey}`, fallback);
    return `${timeframeLabel} ${genericLabel}`;
};

const Statistics = () => {
    const { t } = useTranslation();
    const { statistics } = useApi();
    const [loading, setLoading] = useState(false);
    const [snapshotsRefreshing, setSnapshotsRefreshing] = useState(false);
    /** End of the server's last complete snapshot refresh, shop clock, as it sent it. */
    const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
    const [data, setData] = useState<StatisticsData | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(30, 'day'),
        dayjs(),
    ]);
    const [timeframe, setTimeframe] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
    const [activeTab, setActiveTab] = useState<string>('tables');
    const [chartsSubTab, setChartsSubTab] = useState<string>('contracts');
    const [currency, setCurrency] = useState<'EUR' | 'LEI'>('EUR');
    const currencyLabelMap: Record<string, string> = { LEI: 'RON', EUR: 'EUR' };
    const currencyLabel = currencyLabelMap[currency];

    // BNR EUR→Lei rate used to convert money reference lines when the currency toggles,
    // so a reference saved in one currency stays on the data's scale in the other.
    const { data: grandTotals } = useQuery<{ lei: number; exchangeRate: number | null }>({
        path: '/control-room/grand-totals',
    });
    const exchangeRate = grandTotals?.exchangeRate ?? 5;

    // Business Intelligence state
    const [biLoading, setBiLoading] = useState(false);
    const [biData, setBiData] = useState<BusinessIntelligenceResponse | null>(null);

    // Advanced Analytics state
    const [advancedLoading, setAdvancedLoading] = useState(false);
    const [advancedData, setAdvancedData] = useState<AdvancedAnalyticsResponse | null>(null);

    // Gold 24K tab state
    const [goldValueLoading, setGoldValueLoading] = useState(false);
    const [goldValueData, setGoldValueData] = useState<GoldValueResponse | null>(null);

    // Delphi Clients tab: monthly first-time clients and contracting activity.
    const [clientStatisticsLoading, setClientStatisticsLoading] = useState(false);
    const [clientStatisticsData, setClientStatisticsData] =
        useState<ClientStatisticsResponse | null>(null);

    // Operations charts state (workforce & timing)
    const [operationsLoading, setOperationsLoading] = useState(false);
    const [operationsData, setOperationsData] = useState<OperationsResponse | null>(null);

    // Expenses (Delphi Cheltuieli tab) and monthly goods sold (tsElectroniceCeasuri).
    const [expensesLoading, setExpensesLoading] = useState(false);
    const [expensesData, setExpensesData] = useState<ExpensesResponse | null>(null);
    const [goodsSalesLoading, setGoodsSalesLoading] = useState(false);
    const [goodsSalesData, setGoodsSalesData] = useState<GoodsSalesResponse | null>(null);
    const goodsSoldPoints = useMemo<ChartData[]>(
        () =>
            goodsSalesData?.monthly.map(point => ({
                date: point.period,
                value: point.value,
            })) ?? [],
        [goodsSalesData],
    );

    // Monthly inventory series ported from the Delphi Inventar tab.
    const [inventoryTrendsLoading, setInventoryTrendsLoading] = useState(false);
    const [inventoryTrendsData, setInventoryTrendsData] = useState<InventoryTrendsResponse | null>(
        null,
    );

    // Each inventory card plots one field of one series. Projecting them here keeps the five
    // cards below to their labels alone, and means the arrays are rebuilt when the data changes
    // rather than on every render.
    const silverQuantityPoints = useMemo<ChartData[]>(
        () =>
            inventoryTrendsData?.silver.map(point => ({
                date: point.period,
                value: point.quantity,
            })) ?? [],
        [inventoryTrendsData],
    );
    const silverValuePoints = useMemo<ChartData[]>(
        () =>
            inventoryTrendsData?.silver.map(point => ({
                date: point.period,
                value: point.value,
            })) ?? [],
        [inventoryTrendsData],
    );
    const nonMetalQuantityPoints = useMemo<ChartData[]>(
        () =>
            inventoryTrendsData?.nonMetal.map(point => ({
                date: point.period,
                value: point.quantity,
            })) ?? [],
        [inventoryTrendsData],
    );
    const nonMetalValuePoints = useMemo<ChartData[]>(
        () =>
            inventoryTrendsData?.nonMetal.map(point => ({
                date: point.period,
                value: point.value,
            })) ?? [],
        [inventoryTrendsData],
    );
    const expiredPercentagePoints = useMemo<ChartData[]>(
        () =>
            inventoryTrendsData?.expiredPercentage.map(point => ({
                date: point.period,
                value: point.percentage,
            })) ?? [],
        [inventoryTrendsData],
    );

    // Portfolio flux keeps enough history to compare the selected dates to the same dates
    // thirty days and one year earlier.
    const [portfolioFluxLoading, setPortfolioFluxLoading] = useState(false);
    const [portfolioFluxData, setPortfolioFluxData] = useState<GoldValueResponse | null>(null);

    // Simulations tab state
    const [simulationLoading, setSimulationLoading] = useState(false);
    const [simulationData, setSimulationData] = useState<GoldPriceChangeSimulationResponse | null>(
        null,
    );
    const [simulatedPrices, setSimulatedPrices] = useState<Record<number, number>>({});
    const [globalPriceChangePercent, setGlobalPriceChangePercent] = useState<number>(0);
    const [projectionData, setProjectionData] = useState<CashDemandProjectionResponse | null>(null);
    const [projectionLoading, setProjectionLoading] = useState(false);
    const [projectionMonths, setProjectionMonths] = useState<number>(12);

    const cardDescriptionExtra = (translationKey: string, fallback: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t(translationKey, fallback)}
        </Typography.Text>
    );

    const periodColumn = {
        title: t('statistics.advanced.period', 'Period'),
        dataIndex: 'period',
        key: 'period',
    };

    const createExpiredGoldCurrencyColumns = (currency: 'Eur' | 'Lei', label: string) => [
        {
            title: t(
                `statistics.value.avgActivePassive${currency}`,
                `Avg Active+Passive (${label})`,
            ),
            dataIndex: `avgActivePassive${currency}`,
            key: `avgActivePassive${currency}`,
            align: 'right' as const,
            render: (value: number) => value.toLocaleString('ro-RO'),
        },
        {
            title: t(`statistics.value.expired${currency}`, `Expired (${label})`),
            dataIndex: `expired${currency}`,
            key: `expired${currency}`,
            align: 'right' as const,
            render: (value: number) => value.toLocaleString('ro-RO'),
        },
        {
            title: t(`statistics.value.pct${currency}`, `% (${label})`),
            dataIndex: `pct${currency}`,
            key: `pct${currency}`,
            align: 'right' as const,
            render: (value: number) => `${value.toFixed(2)}%`,
        },
    ];

    const fetchStatistics = useCallback(async () => {
        setLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getData({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                timeframe,
                currency,
            });

            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch statistics data');
            }

            const formatDate = formatDateByTimeframe;

            // Update data mapping to format dates based on timeframe
            const mappedData: StatisticsData = {
                dailyContractValue: (response.data.dailyRevenue || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.revenue ?? 0),
                    projected: item.projected,
                })),
                dailyContractCount: (response.data.dailyRevenue || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    count: Number(item.contracts ?? 0),
                    projected: item.projected,
                })),
                nonMetalContractValue: (response.data.nonMetalContracts || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.value ?? 0),
                })),
                nonMetalContractCount: (response.data.nonMetalContracts || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    count: Number(item.count ?? 0),
                })),
                metalContractValue: (response.data.metalContracts || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.value ?? 0),
                })),
                metalContractCount: (response.data.metalContracts || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    count: Number(item.count ?? 0),
                })),
                topClients: response.data.topClients || [],
                metalsIn24k: (response.data.metalsIn24k || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    grams: Number(item.grams ?? 0),
                })),
                dailyPaidContractsValue: (response.data.dailyPaidContractsValue || []).map(
                    item => ({
                        date: formatDate(item.date, timeframe),
                        value: Number(item.value ?? 0),
                    }),
                ),
                dailyPaidContractsCount: (response.data.dailyPaidContractsCount || []).map(
                    item => ({
                        date: formatDate(item.date, timeframe),
                        count: Number(item.count ?? 0),
                    }),
                ),
                paidCommissionValue: (response.data.paidCommissionValue || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.value ?? 0),
                })),
                paidCommissionCount: (response.data.paidCommissionCount || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.count ?? 0),
                })),
                paidContractCommission: (response.data.paidContractCommission || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.value ?? 0),
                })),
                totalInterestPaid: (response.data.totalInterestPaid || []).map(item => ({
                    date: formatDate(item.date, timeframe),
                    value: Number(item.value ?? 0),
                })),
            };

            setData(mappedData);
        } catch (error) {
            console.error('Failed to fetch statistics:', error);
            message.error(t('statistics.messages.fetchError', 'Failed to fetch statistics data'));
        } finally {
            setLoading(false);
        }
    }, [statistics, dateRange, timeframe, currency, t]);

    // Fetch Business Intelligence data
    const fetchBusinessIntelligence = useCallback(async () => {
        setBiLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getBusinessIntelligence({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                timeframe,
                currency,
            });

            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch business intelligence data');
            }

            setBiData(response.data);
        } catch (error) {
            console.error('Failed to fetch business intelligence:', error);
            message.error(
                t('statistics.bi.fetchError', 'Failed to fetch business intelligence data'),
            );
        } finally {
            setBiLoading(false);
        }
    }, [statistics, dateRange, timeframe, currency, t]);

    // Fetch Advanced Analytics data
    const fetchAdvancedAnalytics = useCallback(async () => {
        setAdvancedLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getAdvancedAnalytics({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                currency,
            });

            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch advanced analytics data');
            }

            setAdvancedData(response.data);
        } catch (error) {
            console.error('Failed to fetch advanced analytics:', error);
            message.error(
                t('statistics.advanced.fetchError', 'Failed to fetch advanced analytics data'),
            );
        } finally {
            setAdvancedLoading(false);
        }
    }, [statistics, dateRange, currency, t]);

    // Fetch Gold Value data
    const fetchGoldValueData = useCallback(async () => {
        setGoldValueLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getGoldValueData({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                timeframe,
            });

            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch gold value data');
            }

            setGoldValueData(response.data);
        } catch (error) {
            console.error('Failed to fetch gold value data:', error);
            message.error(t('statistics.value.fetchError', 'Failed to fetch gold value data'));
        } finally {
            setGoldValueLoading(false);
        }
    }, [statistics, dateRange, timeframe, t]);

    const fetchClientStatistics = useCallback(async () => {
        setClientStatisticsLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getClientStatistics({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
            });

            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch client statistics');
            }

            setClientStatisticsData(response.data);
        } catch (error) {
            console.error('Failed to fetch client statistics:', error);
            message.error(t('statistics.clients.fetchError', 'Failed to fetch client statistics'));
        } finally {
            setClientStatisticsLoading(false);
        }
    }, [statistics, dateRange, t]);

    // Fetch operations analytics (by hour of day + by employee)
    const fetchOperations = useCallback(async () => {
        setOperationsLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getOperationsData({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
            });

            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch operations data');
            }

            setOperationsData(response.data);
        } catch (error) {
            console.error('Failed to fetch operations data:', error);
            message.error(t('statistics.operations.fetchError', 'Failed to fetch operations data'));
        } finally {
            setOperationsLoading(false);
        }
    }, [statistics, dateRange, t]);

    const fetchExpenses = useCallback(async () => {
        setExpensesLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getExpensesData({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
            });
            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch expenses');
            }
            setExpensesData(response.data);
        } catch (error) {
            console.error('Failed to fetch expenses:', error);
            message.error(t('statistics.expenses.fetchError', 'Failed to fetch expenses'));
        } finally {
            setExpensesLoading(false);
        }
    }, [statistics, dateRange, t]);

    const fetchGoodsSales = useCallback(async () => {
        setGoodsSalesLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getGoodsSalesData({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                currency,
            });
            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch goods sales');
            }
            setGoodsSalesData(response.data);
        } catch (error) {
            console.error('Failed to fetch goods sales:', error);
            message.error(t('statistics.goodsSold.fetchError', 'Failed to fetch goods sales'));
        } finally {
            setGoodsSalesLoading(false);
        }
    }, [statistics, dateRange, currency, t]);

    const fetchInventoryTrends = useCallback(async () => {
        setInventoryTrendsLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getInventoryTrends({
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                currency,
            });
            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch inventory trends');
            }
            setInventoryTrendsData(response.data);
        } catch (error) {
            console.error('Failed to fetch inventory trends:', error);
            message.error(t('statistics.inventory.fetchError', 'Failed to fetch inventory trends'));
        } finally {
            setInventoryTrendsLoading(false);
        }
    }, [statistics, dateRange, currency, t]);

    const fetchPortfolioFlux = useCallback(async () => {
        setPortfolioFluxLoading(true);
        try {
            const [startDate, endDate] = dateRange;
            const response = await statistics.getGoldValueData({
                startDate: startDate.subtract(1, 'year').format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
            });
            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch portfolio flux data');
            }
            setPortfolioFluxData(response.data);
        } catch (error) {
            console.error('Failed to fetch portfolio flux:', error);
            message.error(
                t('statistics.portfolioFlux.fetchError', 'Failed to fetch portfolio flux data'),
            );
        } finally {
            setPortfolioFluxLoading(false);
        }
    }, [statistics, dateRange, t]);

    // Load all chart-subtab datasets together when the Charts tab opens.
    const fetchChartsData = useCallback(async () => {
        await Promise.all([
            fetchStatistics(),
            fetchOperations(),
            fetchPortfolioFlux(),
            fetchInventoryTrends(),
            fetchExpenses(),
            fetchGoodsSales(),
        ]);
    }, [
        fetchStatistics,
        fetchOperations,
        fetchPortfolioFlux,
        fetchInventoryTrends,
        fetchExpenses,
        fetchGoodsSales,
    ]);

    // Fetch gold price simulation data
    const fetchGoldPriceSimulation = useCallback(async () => {
        setSimulationLoading(true);
        try {
            const response = await statistics.getGoldPriceSimulation({ currency });
            if (!response.success || !response.data) {
                throw new Error(response.error ?? 'Failed to fetch gold price simulation data');
            }
            setSimulationData(response.data);
            // Initialize simulated prices from current prices
            const initialPrices: Record<number, number> = {};
            for (const item of response.data.karatBreakdown) {
                initialPrices[item.purity] = item.currentPricePerGram;
            }
            setSimulatedPrices(initialPrices);
            setGlobalPriceChangePercent(0);
        } catch (error) {
            console.error('Failed to fetch gold price simulation data:', error);
            message.error(
                t('statistics.simulations.fetchError', 'Failed to fetch simulation data'),
            );
        } finally {
            setSimulationLoading(false);
        }
    }, [statistics, currency, t]);

    // Compute simulated karat breakdown
    const simulatedBreakdown = useMemo((): GoldPriceSimulationKaratBreakdown[] => {
        if (!simulationData) return [];
        return simulationData.karatBreakdown.map(item => {
            const newPrice = simulatedPrices[item.purity] ?? item.currentPricePerGram;
            const newTotalValue = Math.round(item.totalNetGrams * newPrice);
            const additionalCash = newTotalValue - item.currentTotalValue;
            return {
                ...item,
                newPricePerGram: newPrice,
                newTotalValue,
                additionalCashNeeded: additionalCash,
            };
        });
    }, [simulationData, simulatedPrices]);

    // Compute simulated summary
    const simulatedSummary = useMemo(() => {
        if (!simulationData) return null;
        const newTotalValue = simulatedBreakdown.reduce((sum, k) => sum + k.newTotalValue, 0);
        const totalAdditionalCash = simulatedBreakdown.reduce(
            (sum, k) => sum + k.additionalCashNeeded,
            0,
        );
        return {
            ...simulationData.summary,
            newTotalValue,
            totalAdditionalCash,
        };
    }, [simulationData, simulatedBreakdown]);

    // Handle global price change percentage
    const handleGlobalPriceChange = useCallback(
        (percent: number) => {
            setGlobalPriceChangePercent(percent);
            if (!simulationData) return;
            const newPrices: Record<number, number> = {};
            for (const item of simulationData.karatBreakdown) {
                newPrices[item.purity] =
                    Math.round(item.currentPricePerGram * (1 + percent / 100) * 100) / 100;
            }
            setSimulatedPrices(newPrices);
        },
        [simulationData],
    );

    // Handle individual karat price change
    const renderSimulationSummary = useCallback(
        () => (
            <SimulationTableSummaryRow
                simulatedSummary={simulatedSummary}
                currencyLabel={currencyLabel}
            />
        ),
        [simulatedSummary, currencyLabel],
    );

    const handleKaratPriceChange = useCallback((purity: number, newPrice: number | null) => {
        if (newPrice === null) return;
        setSimulatedPrices(prev => ({
            ...prev,
            [purity]: newPrice,
        }));
        // Reset global percent since we're doing individual edits
        setGlobalPriceChangePercent(0);
    }, []);

    // Reset all prices to current
    const handleResetPrices = useCallback(() => {
        if (!simulationData) return;
        const initialPrices: Record<number, number> = {};
        for (const item of simulationData.karatBreakdown) {
            initialPrices[item.purity] = item.currentPricePerGram;
        }
        setSimulatedPrices(initialPrices);
        setGlobalPriceChangePercent(0);
    }, [simulationData]);

    // Fetch cash demand time projection
    const fetchCashDemandProjection = useCallback(
        async (pricePercent?: number, months?: number) => {
            setProjectionLoading(true);
            try {
                const response = await statistics.getCashDemandProjection({
                    priceChangePercent: pricePercent ?? globalPriceChangePercent,
                    monthsAhead: months ?? projectionMonths,
                    currency,
                });
                if (!response.success || !response.data) {
                    throw new Error(response.error ?? 'Failed to fetch cash demand projection');
                }
                setProjectionData(response.data);
            } catch (error) {
                console.error('Failed to fetch cash demand projection:', error);
                message.error(
                    t(
                        'statistics.simulations.projectionFetchError',
                        'Failed to fetch cash demand projection',
                    ),
                );
            } finally {
                setProjectionLoading(false);
            }
        },
        [statistics, globalPriceChangePercent, projectionMonths, currency, t],
    );

    /**
     * Ask the server to bring the snapshots up to date; resolves to whether it took them again.
     * Without `force` the server leaves recent snapshots alone, so opening the page is cheap.
     */
    const refreshStatistics = useCallback(
        async (force = false): Promise<boolean> => {
            setSnapshotsRefreshing(true);
            try {
                const response = await statistics.refresh({ force });
                if (!response.success || !response.data) {
                    return false;
                }
                setLastRefreshedAt(response.data.refreshedAt);
                return response.data.refreshed;
            } catch {
                console.warn('Statistics refresh failed, loading existing data');
                return false;
            } finally {
                setSnapshotsRefreshing(false);
            }
        },
        [statistics],
    );

    const fetchForTab = useCallback(
        (tabKey: string) => {
            const fetchMap: Record<string, (() => Promise<void>) | undefined> = {
                tables: fetchStatistics,
                charts: fetchChartsData,
                clients: fetchClientStatistics,
                'business-intelligence': fetchBusinessIntelligence,
                'advanced-analytics': fetchAdvancedAnalytics,
                gold24k: fetchGoldValueData,
                simulations: fetchGoldPriceSimulation,
            };
            fetchMap[tabKey]?.();
        },
        [
            fetchStatistics,
            fetchChartsData,
            fetchClientStatistics,
            fetchBusinessIntelligence,
            fetchAdvancedAnalytics,
            fetchGoldValueData,
            fetchGoldPriceSimulation,
        ],
    );

    const tabLoadingMap: Record<string, boolean> = useMemo(
        () => ({
            tables: loading,
            charts: loading,
            clients: clientStatisticsLoading,
            'business-intelligence': biLoading,
            'advanced-analytics': advancedLoading,
            gold24k: goldValueLoading,
            simulations: simulationLoading,
        }),
        [
            loading,
            clientStatisticsLoading,
            biLoading,
            advancedLoading,
            goldValueLoading,
            simulationLoading,
        ],
    );
    const isTabLoading = tabLoadingMap[activeTab] ?? loading;

    const handleTabChange = useCallback(
        (key: string) => {
            setActiveTab(key);
            fetchForTab(key);
        },
        [fetchForTab],
    );

    // Stale-while-revalidate: show the stored figures at once, let the server bring the
    // snapshots up to date in the background, and read them again only if it took new ones.
    useEffect(() => {
        fetchStatistics();
        refreshStatistics().then(refreshed => {
            if (refreshed) {
                fetchStatistics();
            }
        });
    }, [fetchStatistics, refreshStatistics]);

    const handleRefreshClick = useCallback(async () => {
        await refreshStatistics(true);
        fetchForTab(activeTab);
    }, [refreshStatistics, fetchForTab, activeTab]);

    // Refetch the visible tab when any global filter changes.
    useEffect(() => {
        fetchForTab(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency, dateRange, timeframe]);

    // Highcharts computes plot geometry (including reference plot lines) from the
    // container size at draw time. When a chart is (re)rendered while its Tabs pane
    // is hidden (display:none) its plot area is zero, so reference lines end up
    // clipped/invisible. Reflow every chart once the Charts pane and its active
    // sub-tab become visible again so the reference lines always show up.
    useEffect(() => {
        if (activeTab !== 'charts') return undefined;
        const raf = requestAnimationFrame(() => {
            Highcharts.charts.forEach(chart => chart?.reflow());
        });
        return () => cancelAnimationFrame(raf);
    }, [activeTab, chartsSubTab, data, loading]);

    // Minimal placeholder data used only when API doesn't provide data
    const sampleData: StatisticsData = useMemo(
        () => ({
            dailyContractValue: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            dailyContractCount: [{ date: dayjs().format('YYYY-MM-DD'), count: 0 }],
            nonMetalContractValue: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            nonMetalContractCount: [{ date: dayjs().format('YYYY-MM-DD'), count: 0 }],
            metalContractValue: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            metalContractCount: [{ date: dayjs().format('YYYY-MM-DD'), count: 0 }],
            topClients: [],
            metalsIn24k: [{ date: dayjs().format('YYYY-MM-DD'), grams: 0 }],
            dailyPaidContractsValue: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            dailyPaidContractsCount: [{ date: dayjs().format('YYYY-MM-DD'), count: 0 }],
            paidCommissionValue: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            paidCommissionCount: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            paidContractCommission: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
            totalInterestPaid: [{ date: dayjs().format('YYYY-MM-DD'), value: 0 }],
        }),
        [],
    );

    const getTimeframedTitle = (baseKey: string, fallback: string) =>
        buildTimeframedTitle(t, timeframe, baseKey, fallback);

    const currentData = data || sampleData;

    // The backend extrapolates the running month of a monthly series to a full
    // month (Delphi parity), so the created-contracts cards say so explicitly.
    const hasProjectedMonth = currentData.dailyContractValue.some(item => item.projected);
    const projectedMonthNote = hasProjectedMonth
        ? cardDescriptionExtra(
              'statistics.charts.projectedNote',
              'Current month extrapolated to a full month',
          )
        : undefined;

    // Helper function to format dates for display based on timeframe
    const formatDateForDisplay = (date: string) => {
        const dateObj = dayjs(date);
        const formatMap: Record<string, string> = {
            yearly: dateObj.format('YYYY'),
            weekly: `${dateObj.format('YYYY-MM-DD')} (Week)`,
            monthly: dateObj.format('YYYY-MM'),
        };
        return formatMap[timeframe] ?? dateObj.format('YYYY-MM-DD');
    };

    // Reusable column builders to reduce duplication
    const dateColumn = {
        title: t('statistics.columns.date', 'Date'),
        dataIndex: 'date',
        key: 'date',
        render: (date: string) => formatDateForDisplay(date),
    };

    const makeMoneyColumn = (titleKey: string, fallback: string) => ({
        title: t(titleKey, fallback),
        dataIndex: 'value',
        key: 'value',
        render: (value: number) =>
            value.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        sorter: (a: { value: number }, b: { value: number }) => a.value - b.value,
    });

    const makeCountColumn = (titleKey: string, fallback: string) => ({
        title: t(titleKey, fallback),
        dataIndex: 'count',
        key: 'count',
        sorter: (a: { count: number }, b: { count: number }) => a.count - b.count,
    });

    // Table columns
    const topClientsColumns = [
        {
            title: t('statistics.columns.clientName', 'Client Name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('statistics.columns.contracts', 'Contracts'),
            dataIndex: 'contracts',
            key: 'contracts',
            sorter: (a: ClientData, b: ClientData) => a.contracts - b.contracts,
        },
        {
            title: `${t('statistics.columns.totalValue', 'Total Value')} (${currencyLabel})`,
            dataIndex: 'value',
            key: 'value',
            render: (value: number) =>
                value.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                }),
            sorter: (a: ClientData, b: ClientData) => a.value - b.value,
        },
    ];

    const dailyContractValueColumns = [
        dateColumn,
        makeMoneyColumn('statistics.columns.value', `Value (${currencyLabel})`),
    ];

    const dailyContractCountColumns = [
        dateColumn,
        makeCountColumn('statistics.columns.count', 'Count'),
    ];

    const nonMetalContractValueColumns = [
        dateColumn,
        makeMoneyColumn('statistics.columns.value', `Value (${currencyLabel})`),
    ];

    const nonMetalContractCountColumns = [
        dateColumn,
        makeCountColumn('statistics.columns.count', 'Count'),
    ];

    const metalsIn24kColumns = [
        {
            title: t('statistics.columns.date', 'Date'),
            dataIndex: 'date',
            key: 'date',
            render: (date: string) => formatDateForDisplay(date),
        },
        {
            title: t('statistics.columns.metalsIn24kGrams', 'Metals 24k (Grams)'),
            dataIndex: 'grams',
            key: 'grams',
            render: (grams: number) =>
                grams.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }),
            sorter: (a: { grams: number }, b: { grams: number }) => a.grams - b.grams,
        },
    ];

    const dailyPaidContractsValueColumns = [
        dateColumn,
        makeMoneyColumn(
            'statistics.columns.paidContractsValue',
            `Paid Contracts Value (${currencyLabel})`,
        ),
    ];

    const dailyPaidContractsCountColumns = [
        dateColumn,
        makeCountColumn('statistics.columns.paidContractsCount', 'Paid Contracts Count'),
    ];

    return (
        <div className="statistics-compressed" style={{ padding: '12px' }}>
            <div
                className="page-header"
                style={{ '--page-header-accent': '#0d9488' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <BarChartOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('statistics.title', 'Statistics')}
                        </Title>
                        <Text type="secondary">
                            {t(
                                'statistics.subtitle',
                                'Business intelligence, trends and portfolio analytics',
                            )}
                        </Text>
                    </div>
                </div>
            </div>

            <Card>
                <Space wrap>
                    <RangePicker
                        value={dateRange}
                        onChange={dates => dates && setDateRange(dates as [Dayjs, Dayjs])}
                        format="YYYY-MM-DD"
                    />
                    <Space.Compact>
                        <Button
                            size="small"
                            onClick={() => setDateRange([dayjs('2000-01-01'), dayjs()])}
                        >
                            {t('statistics.timeframe.allTime', 'All time')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setDateRange([dayjs().subtract(10, 'year'), dayjs()])}
                        >
                            {t('statistics.timeframe.10years', '10Y')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setDateRange([dayjs().subtract(5, 'year'), dayjs()])}
                        >
                            {t('statistics.timeframe.5years', '5Y')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setDateRange([dayjs().subtract(1, 'year'), dayjs()])}
                        >
                            {t('statistics.timeframe.1year', '1Y')}
                        </Button>
                    </Space.Compact>
                    <Select value={timeframe} onChange={setTimeframe} style={{ width: 110 }}>
                        <Option value="daily">{t('statistics.timeframe.daily', 'Daily')}</Option>
                        <Option value="weekly">{t('statistics.timeframe.weekly', 'Weekly')}</Option>
                        <Option value="monthly">
                            {t('statistics.timeframe.monthly', 'Monthly')}
                        </Option>
                        <Option value="yearly">{t('statistics.timeframe.yearly', 'Yearly')}</Option>
                    </Select>
                    <Button
                        type="primary"
                        onClick={handleRefreshClick}
                        loading={isTabLoading || snapshotsRefreshing}
                    >
                        {t('statistics.refresh', 'Refresh')}
                    </Button>
                    {lastRefreshedAt && (
                        // Shop clock, sent as text by the server: never re-read in the browser zone.
                        <Text type="secondary" data-testid="statistics-last-refreshed">
                            {t('statistics.lastRefreshed', 'Updated at {{time}}', {
                                time: lastRefreshedAt.slice(0, 16),
                            })}
                        </Text>
                    )}
                </Space>
            </Card>

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                size="small"
                tabBarExtraContent={
                    <Segmented
                        value={currency}
                        options={[
                            { label: 'Lei', value: 'LEI' },
                            { label: '€', value: 'EUR' },
                        ]}
                        onChange={value => {
                            setCurrency(value as 'EUR' | 'LEI');
                        }}
                        size="small"
                    />
                }
            >
                <TabPane
                    tab={
                        <span>
                            <TableOutlined />
                            {t('statistics.tables', 'Tables')}
                        </span>
                    }
                    key="tables"
                >
                    <Row gutter={[8, 8]}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={getTimeframedTitle('contractValues', 'Contract Values')}
                                extra={projectedMonthNote}
                            >
                                <Table
                                    columns={dailyContractValueColumns}
                                    dataSource={currentData.dailyContractValue}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={getTimeframedTitle('contractCounts', 'Contract Counts')}
                                extra={projectedMonthNote}
                            >
                                <Table
                                    columns={dailyContractCountColumns}
                                    dataSource={currentData.dailyContractCount}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                        <Col xs={24}>
                            <Card title={t('statistics.cards.topClients', 'Top Clients')}>
                                <Table
                                    columns={topClientsColumns}
                                    dataSource={currentData.topClients}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="name"
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={getTimeframedTitle(
                                    'nonMetalContractValues',
                                    'Non-Metal Contract Values',
                                )}
                            >
                                <Table
                                    columns={nonMetalContractValueColumns}
                                    dataSource={currentData.nonMetalContractValue}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={getTimeframedTitle(
                                    'nonMetalContractCounts',
                                    'Non-Metal Contract Counts',
                                )}
                            >
                                <Table
                                    columns={nonMetalContractCountColumns}
                                    dataSource={currentData.nonMetalContractCount}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        <Col xs={24} lg={12}>
                            <Card title={getTimeframedTitle('metalsIn24k', 'Metals in 24k')}>
                                <Table
                                    columns={metalsIn24kColumns}
                                    dataSource={currentData.metalsIn24k}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={getTimeframedTitle(
                                    'paidContractsValue',
                                    'Paid Contracts Value',
                                )}
                            >
                                <Table
                                    columns={dailyPaidContractsValueColumns}
                                    dataSource={currentData.dailyPaidContractsValue}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={getTimeframedTitle(
                                    'paidContractsCount',
                                    'Paid Contracts Count',
                                )}
                            >
                                <Table
                                    columns={dailyPaidContractsCountColumns}
                                    dataSource={currentData.dailyPaidContractsCount}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={loading}
                                    rowKey="date"
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <BarChartOutlined />
                            {t('statistics.chartsTab', 'Charts')}
                        </span>
                    }
                    key="charts"
                >
                    <Tabs
                        activeKey={chartsSubTab}
                        onChange={setChartsSubTab}
                        size="small"
                        type="card"
                    >
                        <TabPane
                            tab={t('statistics.chartsSub.contracts', 'Contracts & Commissions')}
                            key="contracts"
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'createdContractsValue',
                                            'Created Contracts Value',
                                        )}
                                        extra={projectedMonthNote}
                                    >
                                        <ChartWrapper
                                            chartSeries="createdContractsValue"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <DailyContractValueChart
                                                data={currentData.dailyContractValue}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'createdContractsCount',
                                            'Created Contracts Count',
                                        )}
                                        extra={projectedMonthNote}
                                    >
                                        <ChartWrapper chartSeries="createdContractsCount">
                                            <DailyContractCountChart
                                                data={currentData.dailyContractCount}
                                                loading={loading}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'paidContractsValue',
                                            'Paid Contracts Value',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="dailyPaidContractsValue"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <DailyPaidContractsValueChart
                                                data={currentData.dailyPaidContractsValue}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'paidContractsCount',
                                            'Paid Contracts Count',
                                        )}
                                    >
                                        <ChartWrapper chartSeries="dailyPaidContractsCount">
                                            <DailyPaidContractsCountChart
                                                data={currentData.dailyPaidContractsCount}
                                                loading={loading}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'paidCommissionValue',
                                            'Paid Interest Value',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="paidCommissionValue"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <PaidCommissionValueChart
                                                data={currentData.paidCommissionValue}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'paidCommissionCount',
                                            'Paid Interest Count',
                                        )}
                                    >
                                        <ChartWrapper chartSeries="paidCommissionCount">
                                            <PaidCommissionCountChart
                                                data={currentData.paidCommissionCount}
                                                loading={loading}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'paidContractCommission',
                                            'Redemption Interest',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="paidContractCommission"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <PaidContractCommissionChart
                                                data={currentData.paidContractCommission}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'totalInterestPaid',
                                            'Total Interest Paid',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="totalInterestPaid"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <TotalInterestPaidChart
                                                data={currentData.totalInterestPaid}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane tab={t('statistics.chartsSub.metals', 'Metals')} key="metals">
                            <Row gutter={[8, 8]}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'metalContractValues',
                                            'Metal Contract Values',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="metalContractValues"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <DailyMetalContractValueChart
                                                data={currentData.metalContractValue}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'metalContractCounts',
                                            'Metal Contract Counts',
                                        )}
                                    >
                                        <ChartWrapper chartSeries="metalContractCounts">
                                            <DailyMetalContractCountChart
                                                data={currentData.metalContractCount}
                                                loading={loading}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle('metalsIn24k', 'Metals in 24k')}
                                    >
                                        <ChartWrapper chartSeries="metalsIn24k">
                                            <MetalsIn24kChart
                                                data={currentData.metalsIn24k}
                                                loading={loading}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={t('statistics.chartsSub.electronics', 'Electronics + Others')}
                            key="electronics"
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'nonMetalContractValues',
                                            'Non-Metal Contract Values',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="nonMetalContractValues"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <NonMetalContractValueChart
                                                data={currentData.nonMetalContractValue}
                                                loading={loading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={getTimeframedTitle(
                                            'nonMetalContractCounts',
                                            'Non-Metal Contract Counts',
                                        )}
                                    >
                                        <ChartWrapper chartSeries="nonMetalContractCounts">
                                            <NonMetalContractCountChart
                                                data={currentData.nonMetalContractCount}
                                                loading={loading}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={t(
                                            'statistics.goodsSold.title',
                                            'Monthly Goods Sold Value',
                                        )}
                                        extra={cardDescriptionExtra(
                                            'statistics.goodsSold.description',
                                            'Goods sale invoices per month, excluding credited sales',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="goodsSoldValues"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <CurrencyValueChart
                                                data={goodsSoldPoints}
                                                loading={goodsSalesLoading}
                                                currency={currency}
                                                config={{
                                                    chartType: 'column',
                                                    yAxisTitleKeyEur: 'statistics.goodsSold.axisEur',
                                                    yAxisTitleKeyLei: 'statistics.goodsSold.axisLei',
                                                    yAxisTitleFallbackEur: 'Goods sold (EUR)',
                                                    yAxisTitleFallbackLei: 'Goods sold (Lei)',
                                                    color: '#13c2c2',
                                                    seriesNameKey: 'statistics.goodsSold.series',
                                                    seriesNameFallback: 'Goods sold',
                                                }}
                                            />
                                        </ChartWrapper>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={t('statistics.chartsSub.inventory', 'Inventory')}
                            key="inventory"
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24} lg={12}>
                                    <InventoryTrendCard
                                        kind="silverQuantity"
                                        chartSeries="inventorySilverQuantity"
                                        titleKey="statistics.inventory.silverQuantityTitle"
                                        titleFallback="Monthly Silver Inventory Quantity"
                                        descriptionKey="statistics.inventory.silverQuantityDescription"
                                        descriptionFallback="Pure Ag1000 grams moved to stock"
                                        data={silverQuantityPoints}
                                        loading={inventoryTrendsLoading}
                                        money={false}
                                        currency={currency}
                                        exchangeRate={exchangeRate}
                                    />
                                </Col>
                                <Col xs={24} lg={12}>
                                    <InventoryTrendCard
                                        kind="silverValue"
                                        chartSeries="inventorySilverValue"
                                        titleKey="statistics.inventory.silverValueTitle"
                                        titleFallback="Monthly Silver Inventory Value"
                                        data={silverValuePoints}
                                        loading={inventoryTrendsLoading}
                                        money
                                        currency={currency}
                                        exchangeRate={exchangeRate}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                <Col xs={24} lg={12}>
                                    <InventoryTrendCard
                                        kind="nonMetalQuantity"
                                        chartSeries="inventoryNonMetalQuantity"
                                        titleKey="statistics.inventory.nonMetalQuantityTitle"
                                        titleFallback="Monthly Electronics + Other Inventory Quantity"
                                        descriptionKey="statistics.inventory.nonMetalQuantityDescription"
                                        descriptionFallback="Items moved into retail stock"
                                        data={nonMetalQuantityPoints}
                                        loading={inventoryTrendsLoading}
                                        money={false}
                                        currency={currency}
                                        exchangeRate={exchangeRate}
                                    />
                                </Col>
                                <Col xs={24} lg={12}>
                                    <InventoryTrendCard
                                        kind="nonMetalValue"
                                        chartSeries="inventoryNonMetalValue"
                                        titleKey="statistics.inventory.nonMetalValueTitle"
                                        titleFallback="Monthly Electronics + Other Inventory Value"
                                        data={nonMetalValuePoints}
                                        loading={inventoryTrendsLoading}
                                        money
                                        currency={currency}
                                        exchangeRate={exchangeRate}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                <Col xs={24} lg={12}>
                                    <InventoryTrendCard
                                        kind="expiredPercentage"
                                        chartSeries="inventoryExpiredPercentage"
                                        titleKey="statistics.inventory.expiredPercentageTitle"
                                        titleFallback="Monthly Expired Value as % of Signed Value"
                                        descriptionKey="statistics.inventory.expiredPercentageDescription"
                                        descriptionFallback="Stock value divided by contracts signed in the same month"
                                        data={expiredPercentagePoints}
                                        loading={inventoryTrendsLoading}
                                        money={false}
                                        currency={currency}
                                        exchangeRate={exchangeRate}
                                    />
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={t('statistics.chartsSub.expenses', 'Expenses')}
                            key="expenses"
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24} lg={10}>
                                    <Card
                                        title={t(
                                            'statistics.expenses.byTypeTitle',
                                            'Expenses by type',
                                        )}
                                        extra={cardDescriptionExtra(
                                            'statistics.expenses.byTypeDesc',
                                            'Share of each expense category in the selected period',
                                        )}
                                    >
                                        <ExpensesByTypeChart
                                            data={expensesData?.byType ?? []}
                                            loading={expensesLoading}
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} lg={14}>
                                    <Card
                                        title={t(
                                            'statistics.expenses.monthlyTitle',
                                            'Monthly expenses by category',
                                        )}
                                        extra={cardDescriptionExtra(
                                            'statistics.expenses.monthlyDesc',
                                            'Stacked monthly totals per expense category',
                                        )}
                                    >
                                        <MonthlyExpensesChart
                                            data={expensesData?.monthly ?? []}
                                            loading={expensesLoading}
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                        />
                                        <ChartHint
                                            hintKey="statistics.expenses.hint"
                                            fallbackHint="Expenses are recorded in Lei; the EUR view converts them with the current exchange rate."
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={t('statistics.chartsSub.operations', 'Operations')}
                            key="operations"
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={t(
                                            'statistics.operations.byHourTitle',
                                            'Operations by hour of day',
                                        )}
                                        extra={cardDescriptionExtra(
                                            'statistics.operations.byHourDesc',
                                            'Average operations per hour on a typical day',
                                        )}
                                    >
                                        <OperationsByHourChart
                                            data={operationsData?.byHour ?? []}
                                            loading={operationsLoading}
                                        />
                                        <ChartHint
                                            hintKey="statistics.operations.hints.byHour"
                                            fallbackHint="Shows the Delphi-style average daily count in each hour, using contract times from reports and redemption/prolongation times from invoices."
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} lg={12}>
                                    <Card
                                        title={t(
                                            'statistics.operations.byEmployeeTitle',
                                            'Operations by employee',
                                        )}
                                        extra={cardDescriptionExtra(
                                            'statistics.operations.byEmployeeDesc',
                                            'Total contracts, redemptions and prolongations per staff member',
                                        )}
                                    >
                                        <OperationsByEmployeeChart
                                            data={operationsData?.byEmployee ?? []}
                                            loading={operationsLoading}
                                        />
                                        <ChartHint
                                            hintKey="statistics.operations.hints.byEmployee"
                                            fallbackHint="Combines contracts from reports with redemptions and prolongations from invoices, attributed through each source row's user ID."
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={t('statistics.chartsSub.portfolioFlux', 'Portfolio Flux')}
                            key="portfolio-flux"
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24}>
                                    <Card
                                        title={t(
                                            'statistics.portfolioFlux.title',
                                            'Active + Passive Portfolio Flux',
                                        )}
                                        extra={cardDescriptionExtra(
                                            'statistics.portfolioFlux.description',
                                            'Compare the selected period with the same days 30 days and one year earlier',
                                        )}
                                    >
                                        <ChartWrapper
                                            chartSeries="portfolioFlux"
                                            currency={currency}
                                            exchangeRate={exchangeRate}
                                            convertCurrency
                                        >
                                            <PortfolioFluxChart
                                                data={buildPortfolioFluxPoints(
                                                    portfolioFluxData?.timeSeries ?? [],
                                                    dateRange[0].format('YYYY-MM-DD'),
                                                    dateRange[1].format('YYYY-MM-DD'),
                                                    currency,
                                                )}
                                                loading={portfolioFluxLoading}
                                                currency={currency}
                                            />
                                        </ChartWrapper>
                                        <ChartHint
                                            hintKey="statistics.portfolioFlux.hint"
                                            fallbackHint="Compares daily active plus passive portfolio value with the same dates thirty days and one year earlier. Set a target to use the four-colour portfolio lamp."
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>
                    </Tabs>
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <UserOutlined />
                            {t('statistics.clients.title', 'Clients')}
                        </span>
                    }
                    key="clients"
                >
                    <Row gutter={[8, 8]}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.clients.newClientsTitle',
                                    'Monthly New Clients',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.clients.newClientsDescription',
                                    'Clients grouped by the month of their first contract',
                                )}
                            >
                                <MonthlyNewClientsChart
                                    data={clientStatisticsData?.newClients ?? []}
                                    loading={clientStatisticsLoading}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.clients.activityTitle',
                                    'Monthly Clients with Contracts',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.clients.activityDescription',
                                    'Distinct clients and contracts created in each month',
                                )}
                            >
                                <MonthlyClientActivityChart
                                    data={clientStatisticsData?.activity ?? []}
                                    loading={clientStatisticsLoading}
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <LineChartOutlined />
                            {t('statistics.bi.title', 'Business Intelligence')}
                        </span>
                    }
                    key="business-intelligence"
                >
                    {/* Key Metrics Summary */}
                    {biData && (
                        <Typography.Paragraph
                            type="secondary"
                            style={{ fontSize: 12, marginBottom: 8 }}
                            data-testid="bi-kpi-period-note"
                        >
                            {t(
                                'statistics.bi.kpiPeriodNote',
                                'Contracts created between {{start}} and {{end}}. The redemption rate counts only finished contracts (redeemed or moved to stock).',
                                {
                                    start: dateRange[0].format('DD.MM.YYYY'),
                                    end: dateRange[1].format('DD.MM.YYYY'),
                                },
                            )}
                        </Typography.Paragraph>
                    )}
                    {biData && (
                        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t(
                                            'statistics.bi.loanOfferedPerGram',
                                            'Loan Offered per g',
                                        )}
                                        value={biData.goldPricing.avgPricePerGramEur}
                                        precision={2}
                                        suffix={currencyLabel}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t(
                                            'statistics.bi.pledgedFineGoldGrams',
                                            'Gold Pledged in Period (24K g)',
                                        )}
                                        value={biData.goldPricing.totalFineGoldGrams}
                                        precision={2}
                                        suffix="g"
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('statistics.bi.redemptionRate', 'Redemption Rate')}
                                        value={biData.lifecycle.finishedRedemptionRate}
                                        precision={1}
                                        suffix="%"
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('statistics.bi.avgDuration', 'Avg Duration')}
                                        value={Math.round(biData.lifecycle.avgDurationDays)}
                                        suffix={t('statistics.bi.days', 'days')}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Price Simulation - Impact of Gold Price Changes */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.bi.priceSimulationTitle',
                                    'Gold Price Sensitivity Analysis',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.bi.priceSimulationDesc',
                                    'How price changes affect customer acquisition',
                                )}
                            >
                                <PriceSimulationChart
                                    data={biData?.priceSimulations ?? []}
                                    loading={biLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.bi.hints.priceSimulation"
                                    fallbackHint="This chart shows how different gold buying prices could affect customer acquisition. Higher prices attract more customers but reduce margins. Use this to find the optimal price point for your market."
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.bi.profitabilityTitle',
                                    'Profitability Trends',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.bi.profitabilityDesc',
                                    'Revenue, interest income and net profit over time',
                                )}
                            >
                                <ProfitabilityChart
                                    data={biData?.profitabilityTrends ?? []}
                                    loading={biLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.bi.hints.profitability"
                                    fallbackHint="Track your business financial health over time. Blue bars show total revenue, green line shows interest income, and the area chart displays net profit. Look for upward trends and seasonal patterns."
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Customer Segmentation & Lifecycle */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.bi.customerSegmentsTitle',
                                    'Customer Segments',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.bi.customerSegmentsDesc',
                                    'Customer value tiers: VIP, Gold, Silver, Standard',
                                )}
                            >
                                <CustomerSegmentChart
                                    data={
                                        biData?.customerSegments.map(s => ({
                                            segment: s.segment,
                                            customerCount: Number(s.customer_count),
                                            totalContracts: Number(s.total_contracts),
                                            totalValueRon: Number(s.total_value_ron),
                                            avgContractValueRon: Number(s.avg_contract_value_ron),
                                            redemptionRate: Number(s.redemption_rate),
                                        })) ?? []
                                    }
                                    loading={biLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.bi.hints.customerSegments"
                                    fallbackHint="Customers are grouped by total business value: VIP (top spenders), Gold, Silver, and Standard. Larger segments indicate where most of your revenue comes from. Focus retention efforts on VIP and Gold segments."
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.bi.contractLifecycleTitle',
                                    'Contract Lifecycle',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.bi.contractLifecycleDesc',
                                    'Redemption, extension and inventory rates',
                                )}
                            >
                                <ContractLifecycleChart
                                    data={biData?.lifecycle ?? null}
                                    loading={biLoading}
                                />
                                <ChartHint
                                    hintKey="statistics.bi.hints.contractLifecycle"
                                    fallbackHint="Shows what happens to contracts: redeemed (customer paid back), moved to inventory (became stock), or still active. High redemption rates indicate healthy customer relationships and good risk management."
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Metal Distribution & Top Customers */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.bi.metalDistributionTitle',
                                    'Metal Distribution by Value',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.bi.metalDistributionDesc',
                                    'Business focus by metal type',
                                )}
                            >
                                <MetalDistributionChart
                                    data={biData?.metalDistribution ?? []}
                                    loading={biLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.bi.hints.metalDistribution"
                                    fallbackHint="Displays the breakdown of your metal inventory by type (gold, silver, etc.) and their total value. Larger slices represent your primary business focus. Use this to understand your metal portfolio composition."
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t(
                                    'statistics.bi.topCustomersTitle',
                                    'Top Profitable Customers',
                                )}
                            >
                                <Table<{
                                    client_id: number;
                                    name: string;
                                    contracts: number;
                                    prolongation_interest_ron: number;
                                    redemption_profit_ron: number;
                                    profit_ron: number;
                                }>
                                    columns={[
                                        {
                                            title: t('statistics.bi.customerName', 'Name'),
                                            dataIndex: 'name',
                                            key: 'name',
                                            ellipsis: true,
                                            sorter: (a, b) =>
                                                (a.name ?? '').localeCompare(b.name ?? ''),
                                        },
                                        {
                                            title: t('statistics.bi.contracts', 'Contracts'),
                                            dataIndex: 'contracts',
                                            key: 'contracts',
                                            align: 'right' as const,
                                            sorter: (a, b) =>
                                                Number(a.contracts) - Number(b.contracts),
                                        },
                                        {
                                            title: t(
                                                'statistics.bi.prolongationInterest',
                                                'Prolongation Interest',
                                            ),
                                            dataIndex: 'prolongation_interest_ron',
                                            key: 'prolongation_interest_ron',
                                            align: 'right' as const,
                                            sorter: (a, b) =>
                                                Number(a.prolongation_interest_ron ?? 0) -
                                                Number(b.prolongation_interest_ron ?? 0),
                                            render: (value: number) =>
                                                Number(value).toLocaleString(undefined, {
                                                    maximumFractionDigits: 0,
                                                }) + ` ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.bi.redemptionProfit',
                                                'Redemption Profit',
                                            ),
                                            dataIndex: 'redemption_profit_ron',
                                            key: 'redemption_profit_ron',
                                            align: 'right' as const,
                                            sorter: (a, b) =>
                                                Number(a.redemption_profit_ron ?? 0) -
                                                Number(b.redemption_profit_ron ?? 0),
                                            render: (value: number) =>
                                                Number(value).toLocaleString(undefined, {
                                                    maximumFractionDigits: 0,
                                                }) + ` ${currencyLabel}`,
                                        },
                                        {
                                            title: t('statistics.bi.totalProfit', 'Total Profit'),
                                            dataIndex: 'profit_ron',
                                            key: 'profit_ron',
                                            align: 'right' as const,
                                            sorter: (a, b) =>
                                                Number(a.profit_ron ?? 0) -
                                                Number(b.profit_ron ?? 0),
                                            defaultSortOrder: 'descend' as const,
                                            render: (value: number) =>
                                                Number(value).toLocaleString(undefined, {
                                                    maximumFractionDigits: 0,
                                                }) + ` ${currencyLabel}`,
                                        },
                                    ]}
                                    dataSource={biData?.topCustomers ?? []}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    loading={biLoading}
                                    rowKey="client_id"
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Price Simulation Table */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24}>
                            <Card
                                title={t(
                                    'statistics.bi.simulationTableTitle',
                                    'Price Change Impact Simulation',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.bi.simulationTableDesc',
                                    'Estimated impact of offering different gold prices',
                                )}
                            >
                                <Table
                                    columns={[
                                        {
                                            title: t('statistics.bi.priceChange', 'Price Change'),
                                            dataIndex: 'priceChangePercent',
                                            key: 'priceChangePercent',
                                            render: (value: number) =>
                                                `${value >= 0 ? '+' : ''}${value}%`,
                                        },
                                        {
                                            title: t(
                                                'statistics.bi.simulatedPrice',
                                                'Simulated Price/g',
                                            ),
                                            dataIndex: 'simulatedPricePerGram',
                                            key: 'simulatedPricePerGram',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toFixed(2)} ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.bi.estimatedCustomers',
                                                'Est. Customers/month',
                                            ),
                                            dataIndex: 'estimatedNewCustomers',
                                            key: 'estimatedNewCustomers',
                                            align: 'right' as const,
                                        },
                                        {
                                            title: t(
                                                'statistics.bi.estimatedContracts',
                                                'Est. Contracts/month',
                                            ),
                                            dataIndex: 'estimatedNewContracts',
                                            key: 'estimatedNewContracts',
                                            align: 'right' as const,
                                        },
                                        {
                                            title: t(
                                                'statistics.bi.estimatedRevenue',
                                                'Est. Revenue/month',
                                            ),
                                            dataIndex: 'estimatedNewRevenueRon',
                                            key: 'estimatedNewRevenueRon',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currencyLabel}`,
                                        },
                                    ]}
                                    dataSource={biData?.priceSimulations ?? []}
                                    pagination={false}
                                    size="small"
                                    loading={biLoading}
                                    rowKey="priceChangePercent"
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <RocketOutlined />
                            {t('statistics.advanced.title', 'Advanced Analytics')}
                        </span>
                    }
                    key="advanced-analytics"
                >
                    {/* Business Recommendations */}
                    {advancedData && advancedData.recommendations.length > 0 && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={t(
                                'statistics.advanced.recommendations',
                                'Business Recommendations',
                            )}
                            description={
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {advancedData.recommendations.map(rec => {
                                        const parts = rec.split(':');
                                        const key = parts[0];
                                        const value =
                                            parts.length > 1 ? parts.slice(1).join(':') : '';
                                        const translatedRec = t(`statistics.advanced.${key}`, {
                                            value,
                                            defaultValue: rec,
                                        });
                                        return <li key={rec}>{translatedRec}</li>;
                                    })}
                                </ul>
                            }
                        />
                    )}

                    {/* Key Metrics Summary */}
                    {advancedData && (
                        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t(
                                            'statistics.advanced.portfolioHealth',
                                            'Portfolio Health',
                                        )}
                                        value={advancedData.riskMetrics.portfolioHealthScore}
                                        suffix="/100"
                                        valueStyle={{
                                            color: getHealthColor(
                                                advancedData.riskMetrics.portfolioHealthScore,
                                            ),
                                        }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('statistics.advanced.defaultRate', 'Default Rate')}
                                        value={advancedData.riskMetrics.defaultRatePercent}
                                        precision={1}
                                        suffix="%"
                                        valueStyle={{
                                            color: getDefaultRateColor(
                                                advancedData.riskMetrics.defaultRatePercent,
                                            ),
                                        }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t(
                                            'statistics.advanced.atRiskContracts',
                                            'At-Risk Contracts',
                                        )}
                                        value={advancedData.riskMetrics.atRiskContractsCount}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('statistics.advanced.riskTrend', 'Risk Trend')}
                                        value={t(
                                            `statistics.advanced.trend.${advancedData.riskMetrics.riskTrend}`,
                                            advancedData.riskMetrics.riskTrend,
                                        )}
                                        valueStyle={{
                                            color: getRiskTrendColor(
                                                advancedData.riskMetrics.riskTrend,
                                            ),
                                        }}
                                        prefix={
                                            <Tag
                                                color={getRiskTrendTagColor(
                                                    advancedData.riskMetrics.riskTrend,
                                                )}
                                            >
                                                {getRiskTrendArrow(
                                                    advancedData.riskMetrics.riskTrend,
                                                )}
                                            </Tag>
                                        }
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Seasonality & Weekly Pattern */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={14}>
                            <Card
                                title={t(
                                    'statistics.advanced.seasonalityTitle',
                                    'Monthly Seasonality',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.advanced.seasonalityDesc',
                                    'Identify peak and low business periods',
                                )}
                            >
                                <SeasonalityChart
                                    data={advancedData?.seasonality ?? []}
                                    loading={advancedLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.advanced.hints.seasonality"
                                    fallbackHint="Shows business activity patterns across months. Taller bars indicate busier months. Use this to plan staffing, inventory, and promotions. Peak months may need more resources, while low months might benefit from marketing campaigns."
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={10}>
                            <Card
                                title={t(
                                    'statistics.advanced.weeklyPatternTitle',
                                    'Weekly Pattern',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.advanced.weeklyPatternDesc',
                                    'Best days for business',
                                )}
                            >
                                <WeeklyPatternChart
                                    data={advancedData?.weeklyPattern ?? []}
                                    loading={advancedLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.advanced.hints.weeklyPattern"
                                    fallbackHint="Displays average business activity for each day of the week. Identify your busiest days to optimize staff scheduling and ensure adequate coverage. Consider extended hours on peak days."
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Revenue Forecast & Risk Gauge */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24} lg={16}>
                            <Card
                                title={t(
                                    'statistics.advanced.forecastTitle',
                                    '6-Month Revenue Forecast',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.advanced.forecastDesc',
                                    'Predicted revenue with confidence intervals',
                                )}
                            >
                                <ForecastChart
                                    data={advancedData?.forecast ?? []}
                                    loading={advancedLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.advanced.hints.forecast"
                                    fallbackHint="Predicts future revenue based on historical trends. The solid line shows expected revenue, while the shaded area represents the confidence interval (likely range). Wider intervals indicate higher uncertainty."
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={8}>
                            <Card
                                title={t(
                                    'statistics.advanced.riskGaugeTitle',
                                    'Portfolio Risk Assessment',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.advanced.riskGaugeDesc',
                                    'Overall portfolio health score',
                                )}
                            >
                                <RiskGaugeChart
                                    data={advancedData?.riskMetrics ?? null}
                                    loading={advancedLoading}
                                />
                                <ChartHint
                                    hintKey="statistics.advanced.hints.riskGauge"
                                    fallbackHint="Overall health score of your contract portfolio (0-100). Green zone (70+) indicates healthy operations, yellow (40-70) suggests areas for improvement, red (below 40) requires immediate attention to reduce risk."
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Cash Flow Prediction */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24}>
                            <Card
                                title={t(
                                    'statistics.advanced.cashFlowTitle',
                                    '6-Month Cash Flow Prediction',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.advanced.cashFlowDesc',
                                    'Expected inflows, outflows and cumulative cash position',
                                )}
                            >
                                <CashFlowChart
                                    data={advancedData?.cashFlowPredictions ?? []}
                                    loading={advancedLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.advanced.hints.cashFlow"
                                    fallbackHint="Shows predicted money coming in (green) vs going out (red) over the next 6 months. The line shows your cumulative cash position. Plan for months with negative net flow to ensure adequate liquidity."
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Customer Retention */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24}>
                            <Card
                                title={t(
                                    'statistics.advanced.retentionTitle',
                                    'Customer Retention Analysis',
                                )}
                                extra={cardDescriptionExtra(
                                    'statistics.advanced.retentionDesc',
                                    'New vs returning customers and retention rate over time',
                                )}
                            >
                                <RetentionChart
                                    data={advancedData?.retention ?? []}
                                    loading={advancedLoading}
                                />
                                <ChartHint
                                    hintKey="statistics.advanced.hints.retention"
                                    fallbackHint="Compares new customers vs returning ones over time. The retention rate line shows what percentage of customers come back. Higher retention indicates customer satisfaction and loyalty. Aim for 30%+ retention rate."
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Forecast Data Table */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24}>
                            <Card
                                title={t(
                                    'statistics.advanced.forecastTableTitle',
                                    'Detailed Forecast Data',
                                )}
                            >
                                <Table
                                    columns={[
                                        periodColumn,
                                        {
                                            title: t(
                                                'statistics.advanced.predictedRevenue',
                                                'Predicted Revenue',
                                            ),
                                            dataIndex: 'predictedRevenue',
                                            key: 'predictedRevenue',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.advanced.confidenceRange',
                                                'Confidence Range',
                                            ),
                                            key: 'confidence',
                                            align: 'right' as const,
                                            render: (
                                                _: unknown,
                                                record: {
                                                    confidenceLower: number;
                                                    confidenceUpper: number;
                                                },
                                            ) =>
                                                `${record.confidenceLower.toLocaleString()} - ${record.confidenceUpper.toLocaleString()} ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.advanced.predictedContracts',
                                                'Predicted Contracts',
                                            ),
                                            dataIndex: 'predictedContracts',
                                            key: 'predictedContracts',
                                            align: 'right' as const,
                                        },
                                        {
                                            title: t(
                                                'statistics.advanced.growthRate',
                                                'Growth Rate',
                                            ),
                                            dataIndex: 'growthRate',
                                            key: 'growthRate',
                                            align: 'right' as const,
                                            render: (value: number) => (
                                                <span
                                                    style={{
                                                        color: value >= 0 ? '#52c41a' : '#ff4d4f',
                                                    }}
                                                >
                                                    {value >= 0 ? '+' : ''}
                                                    {value}%
                                                </span>
                                            ),
                                        },
                                    ]}
                                    dataSource={advancedData?.forecast ?? []}
                                    pagination={false}
                                    size="small"
                                    loading={advancedLoading}
                                    rowKey="period"
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Cash Flow Table */}
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24}>
                            <Card
                                title={t(
                                    'statistics.advanced.cashFlowTableTitle',
                                    'Cash Flow Details',
                                )}
                            >
                                <Table
                                    columns={[
                                        periodColumn,
                                        {
                                            title: t(
                                                'statistics.advanced.expectedInflows',
                                                'Expected Inflows',
                                            ),
                                            dataIndex: 'expectedInflows',
                                            key: 'expectedInflows',
                                            align: 'right' as const,
                                            render: (value: number) => (
                                                <span style={{ color: '#52c41a' }}>
                                                    +{value.toLocaleString()} {currencyLabel}
                                                </span>
                                            ),
                                        },
                                        {
                                            title: t(
                                                'statistics.advanced.expectedOutflows',
                                                'Expected Outflows',
                                            ),
                                            dataIndex: 'expectedOutflows',
                                            key: 'expectedOutflows',
                                            align: 'right' as const,
                                            render: (value: number) => (
                                                <span style={{ color: '#ff4d4f' }}>
                                                    -{value.toLocaleString()} {currencyLabel}
                                                </span>
                                            ),
                                        },
                                        {
                                            title: t(
                                                'statistics.advanced.netCashFlow',
                                                'Net Cash Flow',
                                            ),
                                            dataIndex: 'netCashFlow',
                                            key: 'netCashFlow',
                                            align: 'right' as const,
                                            render: (value: number) => (
                                                <span
                                                    style={{
                                                        color: value >= 0 ? '#52c41a' : '#ff4d4f',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {value >= 0 ? '+' : ''}
                                                    {value.toLocaleString()} {currencyLabel}
                                                </span>
                                            ),
                                        },
                                        {
                                            title: t(
                                                'statistics.advanced.cumulativePosition',
                                                'Cumulative Position',
                                            ),
                                            dataIndex: 'cumulativeCashPosition',
                                            key: 'cumulativeCashPosition',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toLocaleString()} ${currencyLabel}`,
                                        },
                                    ]}
                                    dataSource={advancedData?.cashFlowPredictions ?? []}
                                    pagination={false}
                                    size="small"
                                    loading={advancedLoading}
                                    rowKey="period"
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <GoldOutlined />
                            {t('statistics.value.title', 'Gold 24K')}
                        </span>
                    }
                    key="gold24k"
                >
                    {/* Gold-only snapshot summary cards, values, and expired gold % */}
                    {goldValueData?.snapshot && (
                        <>
                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.active24k',
                                                'Active (24K g)',
                                            )}
                                            value={goldValueData.snapshot.active24k}
                                            precision={2}
                                            suffix="g"
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.passive24k',
                                                'Passive (24K g)',
                                            )}
                                            value={goldValueData.snapshot.passive24k}
                                            precision={2}
                                            suffix="g"
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.expired24k',
                                                'Expired (24K g)',
                                            )}
                                            value={goldValueData.snapshot.expired24k}
                                            precision={2}
                                            suffix="g"
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.activePassive24k',
                                                'Active + Passive (24K g)',
                                            )}
                                            value={goldValueData.snapshot.activePassive24k}
                                            precision={2}
                                            suffix="g"
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={
                                                {
                                                    EUR: t(
                                                        'statistics.value.avgGramEur',
                                                        'Avg 24K Value (EUR)',
                                                    ),
                                                    LEI: t(
                                                        'statistics.value.avgGramRon',
                                                        'Avg 24K Value (RON)',
                                                    ),
                                                }[currency]
                                            }
                                            value={
                                                {
                                                    EUR: goldValueData.snapshot.avgGramValueEur,
                                                    LEI: goldValueData.snapshot.avgGramValueRon,
                                                }[currency]
                                            }
                                            precision={2}
                                            suffix={currencyLabel}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.totalEur',
                                                'Total Value (EUR)',
                                            )}
                                            value={Math.round(
                                                goldValueData.snapshot.totalValueEur ?? 0,
                                            )}
                                            precision={0}
                                            suffix="EUR"
                                            valueStyle={{ color: '#13c2c2' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.totalRon',
                                                'Total Value (RON)',
                                            )}
                                            value={Math.round(
                                                goldValueData.snapshot.totalValueRon ?? 0,
                                            )}
                                            precision={0}
                                            suffix="RON"
                                            valueStyle={{ color: '#13c2c2' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.value.expiredInventory',
                                                'Expired Gold Value',
                                            )}
                                            value={Math.round(
                                                {
                                                    EUR: goldValueData.snapshot
                                                        .expiredInventoryValueEur,
                                                    LEI: goldValueData.snapshot
                                                        .expiredInventoryValueRon,
                                                }[currency],
                                            )}
                                            precision={0}
                                            suffix={currencyLabel}
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={12} sm={8} lg={4}>
                                    <Card>
                                        <Statistic
                                            title={
                                                {
                                                    EUR: t(
                                                        'statistics.value.expiredGoldPctEur',
                                                        'Expired Gold % (EUR)',
                                                    ),
                                                    LEI: t(
                                                        'statistics.value.expiredGoldPctRon',
                                                        'Expired Gold % (RON)',
                                                    ),
                                                }[currency]
                                            }
                                            value={
                                                {
                                                    EUR: goldValueData.snapshot
                                                        .expiredGoldPctOfMonthlyAvgEur,
                                                    LEI: goldValueData.snapshot
                                                        .expiredGoldPctOfMonthlyAvgRon,
                                                }[currency]
                                            }
                                            precision={2}
                                            suffix="%"
                                            valueStyle={{ color: '#cf1322' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Monthly expired gold % chart and breakdown table */}
                    {goldValueData?.monthlyExpiredGoldPct &&
                        goldValueData.monthlyExpiredGoldPct.length > 0 && (
                            <>
                                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                    <Col xs={24}>
                                        <Card
                                            title={t(
                                                'statistics.value.expiredGoldPctChartTitle',
                                                'Expired Gold Value & % Over Time',
                                            )}
                                        >
                                            <ExpiredGoldPctChart
                                                data={goldValueData.monthlyExpiredGoldPct}
                                                loading={goldValueLoading}
                                                currency={currency}
                                            />
                                            <ChartHint
                                                hintKey="statistics.value.hints.expiredGoldPct"
                                                fallbackHint="Shows expired gold value in EUR and RON (bars) alongside the expired gold percentage relative to active+passive portfolio (lines). Use this to monitor how expired gold changes over time."
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                    <Col xs={24}>
                                        <Card
                                            title={t(
                                                'statistics.value.monthlyExpiredGoldPctTitle',
                                                'Expired Gold % of Monthly Avg Active+Passive',
                                            )}
                                        >
                                            <Table<MonthlyExpiredGoldPctData>
                                                dataSource={goldValueData.monthlyExpiredGoldPct}
                                                rowKey="month"
                                                size="small"
                                                pagination={false}
                                                scroll={{ x: 700 }}
                                                columns={[
                                                    {
                                                        title: t('statistics.value.month', 'Month'),
                                                        dataIndex: 'month',
                                                        key: 'month',
                                                        width: 100,
                                                    },
                                                    ...createExpiredGoldCurrencyColumns(
                                                        {
                                                            EUR: 'Eur' as const,
                                                            LEI: 'Lei' as const,
                                                        }[currency],
                                                        currencyLabel,
                                                    ),
                                                ]}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}

                    {/* Gold by status chart */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24}>
                            <Card
                                title={t(
                                    'statistics.value.goldByStatusTitle',
                                    'Gold Inventory by Status (24K grams)',
                                )}
                            >
                                <GoldValueMultiChart
                                    data={goldValueData?.timeSeries ?? []}
                                    loading={goldValueLoading}
                                />
                                <ChartHint
                                    hintKey="statistics.value.hints.goldByStatus"
                                    fallbackHint="Shows gold inventory in 24K grams broken down by contract status: Active (green), Passive (yellow), and Expired (red). Stacked area shows the total gold held."
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane
                    tab={
                        <span>
                            <ExperimentOutlined />
                            {t('statistics.simulations.title', 'Simulations')}
                        </span>
                    }
                    key="simulations"
                >
                    {simulationData && (
                        <>
                            <Card style={{ marginBottom: 16 }}>
                                <Button onClick={handleResetPrices}>
                                    {t('statistics.simulations.reset', 'Reset to Current Prices')}
                                </Button>
                            </Card>

                            {/* Global price adjustment slider */}
                            <Card
                                title={t(
                                    'statistics.simulations.goldPriceSimTitle',
                                    'Gold Price Change Simulation',
                                )}
                                style={{ marginBottom: 16 }}
                            >
                                <Alert
                                    message={t(
                                        'statistics.simulations.description',
                                        'Simulate the cash demand impact when gold pawn/purchase prices change. Existing active and passive gold contracts would need to be re-evaluated at the new price, generating additional cash requirements.',
                                    )}
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />

                                <Row gutter={[16, 16]} align="middle">
                                    <Col xs={24} lg={12}>
                                        <Typography.Text strong>
                                            {t(
                                                'statistics.simulations.globalPriceChange',
                                                'Global Price Change (%)',
                                            )}
                                        </Typography.Text>
                                        <Row gutter={8} align="middle">
                                            <Col flex="auto">
                                                <Slider
                                                    min={-30}
                                                    max={50}
                                                    step={1}
                                                    value={globalPriceChangePercent}
                                                    onChange={handleGlobalPriceChange}
                                                    marks={{
                                                        '-30': '-30%',
                                                        '-15': '-15%',
                                                        0: '0%',
                                                        15: '+15%',
                                                        30: '+30%',
                                                        50: '+50%',
                                                    }}
                                                />
                                            </Col>
                                            <Col>
                                                <InputNumber
                                                    min={-50}
                                                    max={100}
                                                    step={1}
                                                    value={globalPriceChangePercent}
                                                    onChange={value =>
                                                        handleGlobalPriceChange(value ?? 0)
                                                    }
                                                    formatter={value => `${value}%`}
                                                    parser={value =>
                                                        Number(value?.replace('%', '') ?? 0)
                                                    }
                                                    style={{ width: 100 }}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        <Row gutter={[16, 16]}>
                                            <Col xs={12}>
                                                <Statistic
                                                    title={t(
                                                        'statistics.simulations.currentPortfolioValue',
                                                        `Current Portfolio Value (${currencyLabel})`,
                                                    )}
                                                    value={simulatedSummary?.currentTotalValue ?? 0}
                                                    precision={0}
                                                    valueStyle={{ color: '#1890ff' }}
                                                />
                                            </Col>
                                            <Col xs={12}>
                                                <Statistic
                                                    title={t(
                                                        'statistics.simulations.additionalCashNeeded',
                                                        `Additional Cash Needed (${currencyLabel})`,
                                                    )}
                                                    value={
                                                        simulatedSummary?.totalAdditionalCash ?? 0
                                                    }
                                                    precision={0}
                                                    valueStyle={{
                                                        color:
                                                            (simulatedSummary?.totalAdditionalCash ??
                                                                0) > 0
                                                                ? '#cf1322'
                                                                : '#3f8600',
                                                    }}
                                                    prefix={
                                                        (simulatedSummary?.totalAdditionalCash ??
                                                            0) > 0
                                                            ? '+'
                                                            : ''
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Per-karat price adjustment */}
                            <Card
                                title={t(
                                    'statistics.simulations.perKaratPrices',
                                    'Price per Gram by Karat',
                                )}
                                style={{ marginBottom: 16 }}
                            >
                                <Row gutter={[16, 16]}>
                                    {simulationData.karatBreakdown
                                        .filter(item =>
                                            [999.99, 750, 585].some(
                                                target => Math.abs(item.purity - target) < 5,
                                            ),
                                        )
                                        .map(item => (
                                            <Col xs={24} sm={12} md={8} key={item.purity}>
                                                <Card
                                                    size="small"
                                                    title={item.karatLabel}
                                                    style={{
                                                        borderColor:
                                                            (simulatedPrices[item.purity] ??
                                                                item.currentPricePerGram) !==
                                                            item.currentPricePerGram
                                                                ? '#faad14'
                                                                : undefined,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{ width: '100%' }}
                                                    >
                                                        <div>
                                                            <Typography.Text type="secondary">
                                                                {t(
                                                                    'statistics.simulations.currentPrice',
                                                                    'Current',
                                                                )}
                                                                :{' '}
                                                                {item.currentPricePerGram.toFixed(
                                                                    2,
                                                                )}{' '}
                                                                {currencyLabel}/g
                                                            </Typography.Text>
                                                        </div>
                                                        <div>
                                                            <Typography.Text strong>
                                                                {t(
                                                                    'statistics.simulations.newPrice',
                                                                    'New Price',
                                                                )}
                                                                :
                                                            </Typography.Text>
                                                            <InputNumber
                                                                min={0}
                                                                step={0.5}
                                                                precision={2}
                                                                value={
                                                                    simulatedPrices[item.purity] ??
                                                                    item.currentPricePerGram
                                                                }
                                                                onChange={value =>
                                                                    handleKaratPriceChange(
                                                                        item.purity,
                                                                        value,
                                                                    )
                                                                }
                                                                style={{ width: '100%' }}
                                                                addonAfter={`${currencyLabel}/g`}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Typography.Text type="secondary">
                                                                {t(
                                                                    'statistics.simulations.inventory',
                                                                    'Inventory',
                                                                )}
                                                                :{' '}
                                                            </Typography.Text>
                                                            <Tooltip
                                                                title={t(
                                                                    'statistics.simulations.netGramsTooltip',
                                                                    'Net grams (after stone deduction)',
                                                                )}
                                                            >
                                                                <Typography.Text>
                                                                    {item.totalNetGrams.toFixed(2)}{' '}
                                                                    g ({item.totalContracts}{' '}
                                                                    {t(
                                                                        'statistics.simulations.contracts',
                                                                        'contracts',
                                                                    )}
                                                                    )
                                                                </Typography.Text>
                                                            </Tooltip>
                                                        </div>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        ))}
                                </Row>
                            </Card>

                            {/* Summary statistics */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={12} sm={6}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.simulations.totalContracts',
                                                'Active Gold Contracts',
                                            )}
                                            value={simulatedSummary?.totalContracts ?? 0}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.simulations.totalGoldGrams',
                                                'Total Gold (net g)',
                                            )}
                                            value={simulatedSummary?.totalNetGoldGrams ?? 0}
                                            precision={2}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.simulations.newPortfolioValue',
                                                `New Portfolio Value (${currencyLabel})`,
                                            )}
                                            value={simulatedSummary?.newTotalValue ?? 0}
                                            precision={0}
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Card>
                                        <Statistic
                                            title={t(
                                                'statistics.simulations.cashDemandChange',
                                                `Cash Demand Change (${currencyLabel})`,
                                            )}
                                            value={simulatedSummary?.totalAdditionalCash ?? 0}
                                            precision={0}
                                            valueStyle={{
                                                color:
                                                    (simulatedSummary?.totalAdditionalCash ?? 0) > 0
                                                        ? '#cf1322'
                                                        : '#3f8600',
                                            }}
                                            prefix={
                                                (simulatedSummary?.totalAdditionalCash ?? 0) > 0
                                                    ? '+'
                                                    : ''
                                            }
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Chart */}
                            <Card
                                title={t(
                                    'statistics.simulations.chartTitle',
                                    'Value Comparison by Karat',
                                )}
                                style={{ marginBottom: 16 }}
                            >
                                <GoldPriceChangeSimulationChart
                                    data={simulatedBreakdown}
                                    loading={simulationLoading}
                                    currency={currency}
                                />
                                <ChartHint
                                    hintKey="statistics.simulations.hints.priceChange"
                                    fallbackHint="Shows the current vs simulated gold portfolio value per karat. The red bars indicate the additional cash that would be needed when existing contracts are re-evaluated at new prices."
                                />
                            </Card>

                            {/* Cash Demand Time Projection */}
                            <Card
                                title={t(
                                    'statistics.simulations.projectionTitle',
                                    'Cash Demand Over Time',
                                )}
                                style={{ marginBottom: 16 }}
                                extra={
                                    <Space>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {t(
                                                'statistics.simulations.projectionMonths',
                                                'Months ahead',
                                            )}
                                            :
                                        </Typography.Text>
                                        <Select
                                            value={projectionMonths}
                                            onChange={value => {
                                                setProjectionMonths(value);
                                                fetchCashDemandProjection(
                                                    globalPriceChangePercent,
                                                    value,
                                                );
                                            }}
                                            style={{ width: 80 }}
                                            size="small"
                                        >
                                            <Option value={6}>6</Option>
                                            <Option value={12}>12</Option>
                                            <Option value={18}>18</Option>
                                            <Option value={24}>24</Option>
                                        </Select>
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() =>
                                                fetchCashDemandProjection(
                                                    globalPriceChangePercent,
                                                    projectionMonths,
                                                )
                                            }
                                            loading={projectionLoading}
                                        >
                                            {t(
                                                'statistics.simulations.loadProjection',
                                                'Load Projection',
                                            )}
                                        </Button>
                                    </Space>
                                }
                            >
                                <Alert
                                    message={t(
                                        'statistics.simulations.projectionDescription',
                                        'Shows month-by-month how much additional cash you would need as contracts expire and get renewed at the new price. Uses the global price change % from above.',
                                    )}
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />

                                {projectionData ? (
                                    <>
                                        <CashDemandProjectionChart
                                            data={projectionData.months}
                                            loading={projectionLoading}
                                            priceChangePercent={globalPriceChangePercent}
                                            currency={currency}
                                        />

                                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                            <Col xs={12} sm={6}>
                                                <Statistic
                                                    title={t(
                                                        'statistics.simulations.projectedContracts',
                                                        'Contracts Expiring',
                                                    )}
                                                    value={projectionData.totalContractsProjected}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <Statistic
                                                    title={t(
                                                        'statistics.simulations.projectedGrams',
                                                        'Net Gold Expiring (g)',
                                                    )}
                                                    value={projectionData.totalNetGramsProjected}
                                                    precision={2}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <Statistic
                                                    title={t(
                                                        'statistics.simulations.projectedCurrentValue',
                                                        `Current Value (${currencyLabel})`,
                                                    )}
                                                    value={projectionData.totalCurrentValue}
                                                    precision={0}
                                                    valueStyle={{ color: '#1890ff' }}
                                                />
                                            </Col>
                                            <Col xs={12} sm={6}>
                                                <Statistic
                                                    title={t(
                                                        'statistics.simulations.projectedAdditionalCash',
                                                        `Total Additional Cash (${currencyLabel})`,
                                                    )}
                                                    value={projectionData.totalAdditionalCash}
                                                    precision={0}
                                                    valueStyle={{
                                                        color:
                                                            projectionData.totalAdditionalCash > 0
                                                                ? '#cf1322'
                                                                : '#3f8600',
                                                    }}
                                                    prefix={
                                                        projectionData.totalAdditionalCash > 0
                                                            ? '+'
                                                            : ''
                                                    }
                                                />
                                            </Col>
                                        </Row>

                                        <ChartHint
                                            hintKey="statistics.simulations.hints.projection"
                                            fallbackHint="This chart shows the projected additional cash demand over time. Yellow bars represent the monthly cash needed as contracts expire and are renewed at the new price. The red area shows the cumulative total. The dashed blue line shows how many contracts expire each month."
                                        />
                                    </>
                                ) : (
                                    <Typography.Text type="secondary">
                                        {t(
                                            'statistics.simulations.projectionEmpty',
                                            'Click "Load Projection" to see how cash demand evolves over time with the current price change settings.',
                                        )}
                                    </Typography.Text>
                                )}
                            </Card>

                            {/* Detailed breakdown table */}
                            <Card
                                title={t(
                                    'statistics.simulations.detailsTable',
                                    'Detailed Breakdown',
                                )}
                            >
                                <Table<GoldPriceSimulationKaratBreakdown>
                                    dataSource={simulatedBreakdown}
                                    rowKey="purity"
                                    size="small"
                                    pagination={false}
                                    scroll={{ x: 900 }}
                                    columns={[
                                        {
                                            title: t('statistics.simulations.karat', 'Karat'),
                                            dataIndex: 'karatLabel',
                                            key: 'karatLabel',
                                            width: 80,
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.netGrams',
                                                'Net Grams',
                                            ),
                                            dataIndex: 'totalNetGrams',
                                            key: 'totalNetGrams',
                                            align: 'right' as const,
                                            render: (value: number) => value.toFixed(2),
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.contractsCol',
                                                'Contracts',
                                            ),
                                            dataIndex: 'totalContracts',
                                            key: 'totalContracts',
                                            align: 'right' as const,
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.currentPriceCol',
                                                'Current Price/g',
                                            ),
                                            dataIndex: 'currentPricePerGram',
                                            key: 'currentPricePerGram',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toFixed(2)} ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.newPriceCol',
                                                'New Price/g',
                                            ),
                                            dataIndex: 'newPricePerGram',
                                            key: 'newPricePerGram',
                                            align: 'right' as const,
                                            render: (
                                                value: number,
                                                record: GoldPriceSimulationKaratBreakdown,
                                            ) => (
                                                <Typography.Text
                                                    strong
                                                    style={{
                                                        color:
                                                            value !== record.currentPricePerGram
                                                                ? '#faad14'
                                                                : undefined,
                                                    }}
                                                >
                                                    {value.toFixed(2)} {currencyLabel}
                                                </Typography.Text>
                                            ),
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.currentValueCol',
                                                'Current Value',
                                            ),
                                            dataIndex: 'currentTotalValue',
                                            key: 'currentTotalValue',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toLocaleString('ro-RO')} ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.newValueCol',
                                                'New Value',
                                            ),
                                            dataIndex: 'newTotalValue',
                                            key: 'newTotalValue',
                                            align: 'right' as const,
                                            render: (value: number) =>
                                                `${value.toLocaleString('ro-RO')} ${currencyLabel}`,
                                        },
                                        {
                                            title: t(
                                                'statistics.simulations.additionalCashCol',
                                                'Additional Cash',
                                            ),
                                            dataIndex: 'additionalCashNeeded',
                                            key: 'additionalCashNeeded',
                                            align: 'right' as const,
                                            render: (value: number) => (
                                                <Tag color={getAdditionalCashTagColor(value)}>
                                                    {value > 0 ? '+' : ''}
                                                    {value.toLocaleString('ro-RO')} {currencyLabel}
                                                </Tag>
                                            ),
                                        },
                                    ]}
                                    summary={renderSimulationSummary}
                                />
                            </Card>
                        </>
                    )}
                </TabPane>
            </Tabs>
        </div>
    );
};

export default Statistics;
